import { formatDate, ProviderPatientClaimStatement, ProviderPatientStatement } from '@enter/core';
import { formatLedgerMoney, parseLedgerAmount } from '../utils/statement-format.utils';

export { formatLedgerMoney, parseLedgerAmount };

export type ApplyMode = 'auto' | 'manual';

export type StatementClaimOption = {
    id: string;
    externalId?: string;
    fromDos?: string;
    balance: number;
    pendingAdjudication?: boolean;
};

export const sortClaimsByOldestDos = (a: StatementClaimOption, b: StatementClaimOption): number => {
    const aDos = a.fromDos || '';
    const bDos = b.fromDos || '';
    if (aDos === bDos) return (a.externalId || '').localeCompare(b.externalId || '');
    return aDos < bDos ? -1 : 1;
};

export const toStatementClaimOption = (
    claim: ProviderPatientClaimStatement,
): StatementClaimOption | null => {
    if (!claim.id) return null;
    return {
        id: claim.id,
        externalId: claim.externalId,
        fromDos: claim.fromDos,
        balance: parseLedgerAmount(claim.balanceDue?.amount),
        pendingAdjudication: claim.pendingAdjudication,
    };
};

export const getOpenBalanceClaims = (
    patientStatement?: ProviderPatientStatement,
): StatementClaimOption[] => {
    const claims = (patientStatement?.claims || [])
        .map(toStatementClaimOption)
        .filter((claim): claim is StatementClaimOption => !!claim && claim.balance > 0);
    return claims.sort(sortClaimsByOldestDos);
};

export const resolveTargetClaims = (
    openClaims: StatementClaimOption[],
    applyMode: ApplyMode,
    selectedClaimIds: Record<string, boolean>,
): StatementClaimOption[] => {
    if (applyMode === 'auto') {
        return openClaims;
    }
    return openClaims.filter(claim => selectedClaimIds[claim.id]);
};

/** FIFO-split amount across claims by open balance; remainder goes to last claim. */
export const allocateAmountAcrossClaims = (
    claims: StatementClaimOption[],
    totalAmount: number,
): Array<{ claimId: string; amount: number }> => {
    if (!claims.length || totalAmount <= 0) return [];

    const allocations: Array<{ claimId: string; amount: number }> = [];
    let remaining = totalAmount;

    claims.forEach((claim, index) => {
        const isLast = index === claims.length - 1;
        if (remaining <= 0) return;

        let apply = remaining;
        if (!isLast && claim.balance > 0) {
            apply = Math.min(remaining, claim.balance);
        }

        if (apply > 0) {
            allocations.push({ claimId: claim.id, amount: Number(apply.toFixed(2)) });
            remaining = Number((remaining - apply).toFixed(2));
        }
    });

    return allocations;
};

export const buildFifoNote = (openClaims: StatementClaimOption[]): string => {
    if (!openClaims.length) {
        return 'No claims currently carry an open balance.';
    }

    const first = openClaims[0];
    const dosLabel = first.fromDos ? formatDate(first.fromDos, 'MMM dd, yyyy') : '—';
    return `ENTER applies the amount to the oldest claim with an open balance (#${
        first.externalId || '—'
    } · ${dosLabel}), then rolls any remainder forward to the next oldest. ${
        openClaims.length
    } claims currently carry a balance.`;
};

/** Every claim on the patient's statement, so a payment can be applied to any of them. */
export const getPayableClaims = (
    patientStatement?: ProviderPatientStatement,
): StatementClaimOption[] => {
    const claims = (patientStatement?.claims || [])
        .map(toStatementClaimOption)
        .filter((claim): claim is StatementClaimOption => !!claim);
    return claims.sort(sortClaimsByOldestDos);
};
